import React, { useState, useEffect } from "react";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Seo from "../Seo";
import { breadcrumbJsonLd } from "../../seoSchema";
import "../../styles/Ordering.css";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { resolveFirestoreImage, useCmsHeroImageReady } from "../../firestoreMedia";

const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
	authDomain: "wishuponaplayhouse.firebaseapp.com",
	projectId: "wishuponaplayhouse",
	storageBucket: "wishuponaplayhouse.firebasestorage.app",
	messagingSenderId: "513377368862",
	appId: "1:513377368862:web:eadda2a0b7bb141ffa670f",
	measurementId: "G-NZ116LS0Q1",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

function Ordering() {
	const [mainTitle, setMainTitle] = useState("Ordering Information");
	const [mainParagraph, setMainParagraph] = useState(
		"To order one of our playhouses or to inquire about a custom playhouse just send us an email or give us a call and we will be able to assist you with your order and answer any questions you may have."
	);
	const [sections, setSections] = useState([
		{
			id: 1,
			title: "Payments:",
			content:
				"A 50% deposit of the cost of the playhouse plus all selected options is required to initiate your order. We accept Visa, Master Card, and American Express. Checks are also accepted.\n\nWhen your deposit is received, finalization of your options, painting and color selections will be made. A scheduled building and installation date will be determined at the time of your order. Our playhouses require approximately 5-7 days to be completed depending on chosen options and weather.",
		},
		{
			id: 2,
			title: "Sales Tax:",
			content: "Denver residence pay 7.65% sales tax.",
		},
		{
			id: 3,
			title: "Color Combinations:",
			content:
				"Selecting paint colors for your playhouse requires 3 to 4 choices. Exterior includes the body, trim, shutters, and interior includes walls and trim. Please refer to Benjamin Moore ® color palette for color names and numbers. If you need help in customizing your colors, we have designers available to create color schemes for you at an additional charge.",
		},
		{
			id: 4,
			title: "Site Preparations:",
			content:
				"Prior to building your playhouse a site must be determined and prepared. The area must be cleared of all debris, roots, grass, rocks etc. The ground must be level due to the weight of each playhouse. We are able to prepare your site for you for an additional charge which will be determined after site inspection.",
		},
		{
			id: 5,
			title: "Final Payment:",
			content:
				"The balance of the invoice cost of the playhouse is due at completion. Payment may be made by Visa, Master Card, American Express or certified check.",
		},
		{
			id: 6,
			title: "Liability:",
			content:
				"Our playhouses are constructed by skilled craftsmen using quality materials designed with durability and safety in mind. We strive to create a safe and fun environment for children to play. Wish Upon a Playhouse assumes no liability after the playhouse has been built on your property. You are responsible for selecting a structure that is appropriate for the developmental level of the children who will use them. Routine inspections, preventive maintenance and play supervision are all the responsibility of the buyer to ensure a play environment that is free of hazard or injury.",
		},
		{
			id: 7,
			title: "Warranty:",
			content:
				"We warrant our playhouses for one year from the date of purchase to repair or replace any part defective in materials or workmanship. This warranty excludes any labor incurred for replacement. We disclaim liability for any buyer alterations, repairs or assembly normal wear and tear, or damages resulting from misuse or abuse.",
		},
	]);

	const [isLoading, setIsLoading] = useState(true);
	const [heroImage, setHeroImage] = useState(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setIsLoading(true);
				const docRef = doc(firestore, "Pages", "OrderingPage");
				const docSnap = await getDoc(docRef);

				if (docSnap.exists()) {
					const data = docSnap.data();

					if (data.mainTitle) setMainTitle(data.mainTitle);
					if (data.mainParagraph) setMainParagraph(data.mainParagraph);
					if (data.sections && data.sections.length > 0)
						setSections(data.sections);
					if (data.heroImage) setHeroImage(data.heroImage);
				}
			} catch (error) {
				console.error("Error fetching ordering content: ", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
		// eslint-disable-next-line
	}, []);

	const orderingHeroUrl = resolveFirestoreImage(heroImage, "/images/sub-hero.png");
	const orderingHeroReady = useCmsHeroImageReady(orderingHeroUrl, !isLoading);

	if (isLoading) {
		return (
			<div className="orderingContainer">
				<Navbar />
				
				<div className="loading">Loading content...</div>
				<Footer />
			</div>
		);
	}

	return (
		<div className="orderingContainer">
			<Seo
				title="How to Order Your Custom Playhouse | Denver, Colorado"
				description="Step-by-step guide to ordering a custom children's playhouse from Wish Upon A Playhouse. From initial design through on-site build in your Denver or Colorado backyard."
				path="/playhouses/ordering"
				jsonLd={breadcrumbJsonLd([
					{ name: "Home", path: "/" },
					{ name: "Playhouses", path: "/playhouses" },
					{ name: "Ordering", path: "/playhouses/ordering" },
				])}
				jsonLdId="ordering-breadcrumb"
			/>
			<Navbar />

		<>
		<div className="sub-hero">
					<div
						className="sub-hero-bg"
						style={{
							backgroundImage: `url('${orderingHeroUrl}')`,
							opacity: orderingHeroReady ? 1 : 0,
							transition: "opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
						}}
					/>
					<div className="sub-hero-overlay" />
					<div className="sub-hero-content">
						<p className="sub-hero-eyebrow">How It Works</p>
						<h1 className="sub-hero-title">{mainTitle}</h1>
					</div>
				</div>

				<div className="ordering-intro">
					<p>{mainParagraph}</p>
				</div>

				<div className="ordering-sections">
					{sections.map((section) => (
						<div key={section.id} className="ord-section">
							<div className="ord-section-header">
								<h3>{section.title}</h3>
							</div>
							<div className="ord-section-body">{section.content}</div>
						</div>
					))}
				</div>

				<div className="ordering-cta-band">
					<div className="ordering-cta-inner">
						<h2 className="ordering-cta-heading">Ready to Place Your Order?</h2>
						<a href="/contact" className="ordering-cta-btn">
							<span>Get in Touch</span>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
							</svg>
						</a>
					</div>
				</div>
		</>

		<div className="spacer"></div>
		<Footer />
		</div>
	);
}

export default Ordering;
